import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { aws } from '../lib/awsdb';
import { vaultWrite } from '../lib/vaultWrite';
import { vaultReveal } from '../lib/vaultRead';
import { logVaultActivity } from '../lib/vaultActivityLog';
import { ModalFrame } from '../ui/modal';
import { IconClose, IconEye, IconLink, IconLock } from '../ui/icons';
import { CATEGORIES, CATEGORY_COLORS, CATEGORY_EXAMPLES } from './categories';
import '../po/drawer.css';

function formatWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
}

// Credential create/edit/view, as a modal — same treatment as the other
// three Vault modals (Filter, Access Log, Manage Access), not a right-side
// slider. Two sections: Details and Access Log, same tab pattern as
// VaultActivityLogModal's Activity/Roster tabs.
export default function VaultCredentialModal({ session, credential, onClose, onSaved }) {
  const isEdit = !!credential?.id;
  const isAdmin = session.role === 'admin';
  // Admins manage (create/edit/archive); everyone else only consumes —
  // views, reveals/copies if their grant allows, reads the log. Non-admins
  // never reach the "new credential" form since +New is hidden for them.
  const canEdit = isAdmin;
  // Admins bypass grants entirely; everyone else's access comes from
  // credential.my_level, set server-side by vault-read's list action.
  const canReveal = isAdmin || credential?.my_level === 'revealer';
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState(() => credential ? { ...credential, password: '' } : {
    site_name: '', category: CATEGORIES[0].key, login_username: '', login_email: '', url: '', password: '', notes: '', tags: ''
  });
  const [activity, setActivity] = useState([]);
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState(null); // plaintext password, once fetched from vault-reveal
  const [revealing, setRevealing] = useState(false);
  const [copied, setCopied] = useState(false);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!isEdit) return;
    loadActivity(credential);
    // Low-stakes view event — logged client-side. Reveal is logged
    // server-side inside vault-reveal, never here (see reveal()).
    logVaultActivity(credential.id, session, { action_type: 'credential_viewed' });
  }, [credential, isEdit]);

  async function loadActivity(c) {
    const { data } = await aws.from('vault_activity_log').select('*').eq('credential_id', c.id).order('created_at', { ascending: false });
    setActivity(data || []);
  }

  async function reveal() {
    setRevealing(true);
    try {
      const { password } = await vaultReveal(session, credential.id);
      setRevealed(password);
      loadActivity(credential); // pick up the server-written credential_revealed row
    } catch (e) {
      alert('Could not reveal password: ' + (e.message || e));
    } finally {
      setRevealing(false);
    }
  }

  // Copy is a distinct, separately-logged event from reveal — Renz wants
  // the log to say specifically who *copied* a password, not just who saw
  // it. The password is already in the client at this point (post-reveal),
  // so this is a plain client-side log, same tier as credential_viewed.
  async function copyPassword() {
    if (!revealed) return;
    await navigator.clipboard.writeText(revealed);
    logVaultActivity(credential.id, session, { action_type: 'credential_copied' });
    setCopied(true);
    loadActivity(credential);
    setTimeout(() => setCopied(false), 2000);
  }

  async function save() {
    if (!canEdit) return;
    if (!form.site_name?.trim()) { alert('Enter a site or company name.'); return; }
    setSaving(true);
    try {
      const row = {
        site_name: form.site_name.trim(),
        category: form.category || CATEGORIES[0].key,
        login_username: form.login_username || null,
        login_email: form.login_email || null,
        url: form.url || null,
        notes: form.notes || null,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      // Blank password on edit leaves the existing ciphertext untouched —
      // moreece-vault only re-encrypts when a non-empty value is sent.
      if (form.password) row.password = form.password;
      if (isEdit) {
        await vaultWrite(session, 'vault_credentials', 'update', row, { column: 'id', value: credential.id });
      } else {
        row.created_by = session.code;
        await vaultWrite(session, 'vault_credentials', 'insert', row);
      }
      setSaving(false); onSaved?.(); onClose();
    } catch (e) {
      setSaving(false);
      alert('Could not save credential: ' + (e.message || e));
    }
  }

  return (
    <ModalFrame onClose={onClose}>
      <motion.div
        role="dialog"
        initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ duration: 0.18 }}
        style={{
          width: 'min(780px, calc(100vw - 48px))',
          maxHeight: 'calc(100vh - 96px)',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(15,20,25,0.22), 0 2px 6px rgba(15,20,25,0.08)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subdued)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--surface-inverted)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <IconLock size={14} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-emphasized)' }}>{isEdit ? form.site_name : 'Add Credential'}</div>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-subdued)', padding: 6, display: 'flex' }}>
            <IconClose size={16} />
          </button>
        </div>

        {isEdit && (
          <div style={{ display: 'flex', gap: 4, padding: '10px 20px 0', flexShrink: 0 }}>
            {[{ key: 'details', label: 'Details' }, { key: 'log', label: `Access Log${activity.length > 0 ? ` (${activity.length})` : ''}` }].map(t => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                  background: tab === t.key ? 'var(--surface-elevated)' : 'transparent',
                  color: tab === t.key ? 'var(--text-emphasized)' : 'var(--text-subdued)',
                }}>{t.label}</button>
            ))}
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px', background: isEdit ? 'var(--surface-elevated)' : '#fff' }}>
          {tab === 'details' && (<div style={{ background: '#fff', borderRadius: isEdit ? 12 : 0, padding: isEdit ? 16 : 0, border: isEdit ? '1px solid var(--border-subdued)' : 'none' }}>
            {/* Wide, two-section layout instead of one tall stacked column —
                left: identity + the credential pair (email, password) you
                actually need to log in; right: secondary fields. */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div>
                {canEdit ? (<>
                  <div className="form-group"><label className="form-label">Site / Company</label>
                    <input className="form-input" value={form.site_name || ''} onChange={e => setField('site_name', e.target.value)} placeholder="e.g. Xero, Shopify Admin" /></div>
                  <div className="form-group"><label className="form-label">Category</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 999, background: CATEGORY_COLORS[form.category] || '#64748b', flexShrink: 0 }} />
                      <select className="form-select" style={{ flex: 1 }} value={form.category || ''} onChange={e => setField('category', e.target.value)}>
                        {CATEGORIES.map(c => (
                          <option key={c.key} value={c.key} title={CATEGORY_EXAMPLES[c.key]}>
                            {c.label}{CATEGORY_EXAMPLES[c.key] ? ` — ${CATEGORY_EXAMPLES[c.key]}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {CATEGORY_EXAMPLES[form.category] && (
                      <div style={{ fontSize: 11, color: 'var(--text-subdued)', marginTop: 6 }}>e.g. {CATEGORY_EXAMPLES[form.category]}</div>
                    )}
                  </div>
                  <div className="form-group"><label className="form-label">Email</label>
                    <input type="email" className="form-input" value={form.login_email || ''} onChange={e => setField('login_email', e.target.value)} placeholder="Login email" /></div>
                </>) : (<>
                  <div className="form-group"><label className="form-label">Site / Company</label>
                    <div className="readonly-value">{form.site_name || '--'}</div></div>
                  <div className="form-group"><label className="form-label">Category</label>
                    <div className="readonly-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 999, background: CATEGORY_COLORS[form.category] || '#64748b', flexShrink: 0 }} />
                      {form.category || 'Other'}
                    </div></div>
                  <div className="form-group"><label className="form-label">Email</label>
                    <div className="readonly-value">{form.login_email || '--'}</div></div>
                </>)}

                <div className="subsection">
                  <div className="subsection-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}><IconLock size={11} /> Password</div>
                  <div className="subsection-body">
                    {isEdit ? (<>
                      <div className="form-group" style={{ marginBottom: canEdit ? 8 : 0 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <div style={{ position: 'relative', flex: 1 }}>
                            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-disabled)', display: 'flex' }}><IconEye size={13} off={!revealed} /></span>
                            <input className="form-input" style={{ paddingLeft: 30, fontFamily: revealed ? 'var(--font-mono)' : 'inherit', letterSpacing: revealed ? 0 : '2px' }}
                              readOnly value={revealed || '••••••••••••'} />
                          </div>
                          {canReveal ? (<>
                            <button type="button" className="btn btn-secondary btn-sm" disabled={revealing} onClick={reveal} style={{ whiteSpace: 'nowrap' }}>
                              {revealing ? 'Revealing...' : revealed ? 'Hide' : 'Reveal'}
                            </button>
                            {revealed && (
                              <button type="button" className="btn btn-secondary btn-sm" onClick={copyPassword} style={{ whiteSpace: 'nowrap' }}>
                                {copied ? 'Copied!' : 'Copy'}
                              </button>
                            )}
                          </>) : (
                            <span style={{ fontSize: 10, color: 'var(--text-disabled)', whiteSpace: 'nowrap' }}>Viewer access only</span>
                          )}
                        </div>
                        {revealed && <div style={{ fontSize: 11, color: 'var(--text-subdued)', marginTop: 6 }}>Every reveal and copy is logged to this credential's Access Log.</div>}
                      </div>
                      {canEdit && (
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <input type="password" className="form-input" value={form.password || ''} onChange={e => setField('password', e.target.value)} placeholder="New password — leave blank to keep current" />
                        </div>
                      )}
                    </>) : (
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <input type="password" className="form-input" value={form.password || ''} onChange={e => setField('password', e.target.value)} placeholder="Enter password" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                {canEdit ? (<>
                  <div className="form-group"><label className="form-label">Username <span className="field-hint">optional</span></label>
                    <input className="form-input" value={form.login_username || ''} onChange={e => setField('login_username', e.target.value)} placeholder="Only if this login uses one" /></div>
                  <div className="form-group">
                    <label className="form-label">Login Link</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input className="form-input" style={{ flex: 1 }} value={form.url || ''} onChange={e => setField('url', e.target.value)} placeholder="https://... (the page you log in at)" />
                      {form.url && (
                        <a href={form.url} target="_blank" rel="noopener noreferrer" title="Open login link"
                          style={{ display: 'flex', flexShrink: 0, padding: 8, color: 'var(--text-subdued)', border: '1px solid var(--border-default)', borderRadius: 8 }}>
                          <IconLink size={13} />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="form-group"><label className="form-label">Tags <span className="field-hint">comma-separated</span></label>
                    <input className="form-input" value={form.tags || ''} onChange={e => setField('tags', e.target.value)} placeholder="Marketing, Ops" /></div>
                  <div className="form-group"><label className="form-label">Notes</label>
                    <textarea className="form-textarea" value={form.notes || ''} onChange={e => setField('notes', e.target.value)} placeholder="Internal notes, recovery info, etc." /></div>
                </>) : (<>
                  <div className="form-group"><label className="form-label">Username</label>
                    <div className="readonly-value">{form.login_username || '--'}</div></div>
                  <div className="form-group"><label className="form-label">Login Link</label>
                    {form.url ? (
                      <div className="readonly-value">
                        <a href={form.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                          {form.url}
                        </a>
                      </div>
                    ) : <div className="readonly-value">--</div>}
                  </div>
                  {form.tags && <div className="form-group"><label className="form-label">Tags</label>
                    <div className="readonly-value">{Array.isArray(form.tags) ? form.tags.join(', ') : form.tags}</div></div>}
                  {form.notes && <div className="form-group"><label className="form-label">Notes</label>
                    <div className="readonly-value" style={{ whiteSpace: 'pre-wrap' }}>{form.notes}</div></div>}
                </>)}
              </div>
            </div>

            {isEdit && credential.created_at && (
              <div className="muted-small" style={{ marginTop: 12 }}>Created {formatWhen(credential.created_at)}{credential.created_by ? ` by ${credential.created_by}` : ''}</div>
            )}
          </div>)}

          {tab === 'log' && isEdit && (
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--border-subdued)', overflow: 'hidden' }}>
              {activity.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 20px', color: 'var(--text-subdued)', fontSize: 13 }}>No access events yet</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      {['Who', 'Action', 'When'].map(h => (
                        <th key={h} style={{ textAlign: 'left', fontSize: 10, fontWeight: 600, color: 'var(--text-subdued)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', padding: '8px 16px', borderBottom: '1px solid var(--border-subdued)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activity.map(entry => (
                      <tr key={entry.id}>
                        <td style={{ padding: '9px 16px', borderBottom: '1px solid var(--border-subdued)', fontWeight: 500, color: 'var(--text-emphasized)', whiteSpace: 'nowrap' }}>{entry.actor_name || 'Someone'}</td>
                        <td style={{ padding: '9px 16px', borderBottom: '1px solid var(--border-subdued)', color: 'var(--text-subdued)' }}>{entry.action_type?.replace(/_/g, ' ')}{entry.detail && ` · ${entry.detail}`}</td>
                        <td style={{ padding: '9px 16px', borderBottom: '1px solid var(--border-subdued)', color: 'var(--text-subdued)', whiteSpace: 'nowrap' }}>{formatWhen(entry.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {tab === 'details' && canEdit && (
          <div style={{ display: 'flex', gap: 8, padding: '14px 20px', borderTop: '1px solid var(--border-subdued)', flexShrink: 0 }}>
            <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" style={{ flex: 1 }} disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        )}
      </motion.div>
    </ModalFrame>
  );
}
